import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../Card/Card';
import { IIabCategory } from '@/pages/Root/services/api/lookBook/lookbook-model-definitions';
import './CoverPage.scss';
import { IImageData } from '@pages/Root/services/api/interfaces.ts';
import { KenBurnsParams, OwnedBy } from '@moment/net/softbox-types.ts';
import KenBurnsAsyncImage from '@moment/components/AsyncImage/KenBurnsAsyncImage.tsx';
import { AD_SIZE, BannerAdSizes, } from '@moment/components/GalleryAd/ad-constants.ts';
import { updatePageData } from '@/utils';
import { AdRenderer } from '@moment/components/AdRenderer/AdRenderer.tsx';
import { GamAd } from '@moment/components/GamAd/GamAd.tsx';
import { Button } from '@moment/components/Button/Button.tsx';
import { ButtonType } from '@moment/components/Button/Button-Constants.ts';
import { useTranslation } from 'react-i18next';

export interface CoverPageProps {
  title?: string;
  description?: string;
  image?: string;
  imageData: IImageData;
  credit?: string;
  kenBurnsParams: KenBurnsParams;
  isActive: boolean;
  doNotTrack: boolean;
  ownedBy?: OwnedBy;
  pageIndex: number;
  isIgnoreParagraphInDescription?: boolean;
  isDark?: boolean;
  caption?: string;
  lookId: string;
  categoryList?: IIabCategory[]; // Kept for targeting
  onScroll?: (isScrolledDown: boolean) => void;
  enableAd: boolean;
  lbtype?: string;
  onNext?: () => void;
  sourceLink?: string;
}

export const CoverPage: FC<CoverPageProps> = ({
  title,
  description,
  image,
  isActive,
  ownedBy,
  pageIndex,
  isIgnoreParagraphInDescription,
  isDark,
  caption,
  lookId,
  categoryList,
  imageData,
  kenBurnsParams,
  onScroll,
  enableAd,
  credit,
  lbtype,
  onNext,
  sourceLink,
}) => {
  const { t } = useTranslation();
  const [adSize, setAdSize] = useState<BannerAdSizes>();
  const [isScrollIndicatorActive, setIsScrollIndicatorActive] = useState(false);
  const [isScrollIndicatorVisible, setIsScrollIndicatorVisible] =
    useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    updatePageData({ title, description });
  }, [title, description]);

  useEffect(() => {
    const adSize = pageIndex > 0 ? AD_SIZE.middle : AD_SIZE.small;
    setAdSize(adSize);
  }, [pageIndex, description]);

  const isEmpty = useMemo(() => {
    // Before: return !(title && description && caption && ownedBy);
    // This was too strict and would hide the card if any single prop was missing.

    // After: The card is considered empty only if ALL key content props are missing.
    return !title && !description && !caption && !ownedBy;
  }, [title, description, caption, ownedBy]);

  const scrollIndicatorContainerClassList = useMemo(() => {
    let baseClassList = 'fm-cover-page__scroll-indicator-container';
    if (isScrollIndicatorActive) {
      baseClassList += ' fm-cover-page__scroll-indicator-container--flex';
    }
    if (isActive) {
      baseClassList += ' appear-from-bottom';
    }
    return baseClassList;
  }, [isActive, isScrollIndicatorActive]);

  const scrollIndicatorClassList = useMemo(() => {
    let baseClassList = 'fm-cover-page__scroll-indicator';
    if (!isScrollIndicatorVisible) {
      baseClassList += ' hidden';
    }
    return baseClassList;
  }, [isScrollIndicatorVisible]);

  const onScrollRef = useRef(onScroll);
  useEffect(() => { onScrollRef.current = onScroll; });

  useEffect(() => {
    const scrollEl = contentRef.current;
    if (!scrollEl) return;

    setIsScrollIndicatorActive(scrollEl.scrollHeight > scrollEl.offsetHeight);

    let settleTimer: ReturnType<typeof setTimeout>;
    let lastEmitted: boolean | undefined;

    const handleScroll = () => {
      const atBottom =
        scrollEl.scrollTop >= scrollEl.scrollHeight - scrollEl.offsetHeight;
      setIsScrollIndicatorVisible(!atBottom);

      // Debounce: report only the settled position. Rubber-band rebounds at
      // the top fire a transient scrollTop=0 followed by a small >0 value;
      // reading scrollTop after the scroll settles avoids that false/true flicker.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const isScrolled = scrollEl.scrollTop > 0;
        if (isScrolled !== lastEmitted) {
          lastEmitted = isScrolled;
          onScrollRef.current?.(isScrolled);
        }
      }, 120);
    };

    scrollEl.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(settleTimer);
      scrollEl.removeEventListener('scroll', handleScroll);
    };
  }, [isEmpty]);

  const extraTargeting = useMemo(() => {
    const targeting: Record<string, string | string[]> = {
      mp_contentid: lookId,
      page_number: `${pageIndex}`,
    };

    if (categoryList?.length) {
      targeting['iab_category_codes'] = categoryList.map((item) => item.code);
      targeting['iab_category_names'] = categoryList.map((item) => item.label);
    }
    return targeting;
  }, [lookId, pageIndex, categoryList]);

  const renderAdComponent = useMemo(() => {
    if (!enableAd || !adSize) {
      return null;
    }

    const gamAd = (
      <GamAd
        adType={'top'}
        adUnitId={`gallery-${lookId}-${pageIndex}`}
        size={adSize.toGamSizing()}
        containerClassName="gallery-ad"
        adBlockClassName="gallery-ad__unit"
        extraTargeting={extraTargeting}
        iabCategories={categoryList?.map((item) => item.code)}
      />
    );

    return (
      <AdRenderer
        placement="top"
        adUnitId={`gallery-${lookId}-${pageIndex}`}
        GamComponent={gamAd}
        dtxAdSize={[adSize]}
        containerClassName="fm-cover-page__dtx--ad--block"
        iabCategories={categoryList?.map((item) => item.code)}
      />
    );
  }, [enableAd, adSize, lookId, pageIndex, extraTargeting]);

  const styleList = useMemo(() => {
    return {
      height: (adSize?.height || 0) + 30,
    } as CSSProperties;
  }, [adSize]);

  const isSpringboard = lbtype === 'springboard';

  const handleSpringboardClick = () => {
    if (sourceLink) {
      window.open(sourceLink, '_blank');
    } else if (onNext) {
      onNext();
    }
  };

  return (
    <div className="fm-cover-page" data-testid="test-fm-cover-page">
      <div
        className="fm-cover-page__item"
        data-testid="test-fm-cover-page__item"
      >
        <div
          className="fm-cover-page__image"
          data-testid="test-fm-cover-page__image"
        >
          {image && (
            <KenBurnsAsyncImage
              src={image}
              alt={title}
              staticSized
              height={imageData.height}
              width={imageData.width}
              imageData={imageData}
              kenBurnsParams={kenBurnsParams}
              activeAnimation={isActive}
            />
          )}
        </div>
        {!isEmpty && (
          <div
            className="fm-cover-page__content"
            data-testid="test-fm-cover-page__content"
            ref={contentRef}
          >
            <Card
              title={title}
              summary={description}
              isIgnoreParagraphInDescription={isIgnoreParagraphInDescription}
              publisherLogo={ownedBy?.logoLarge}
              publisherName={ownedBy?.name}
              darkPublisherLogo={ownedBy?.logoDark}
              isDark={isDark}
              caption={caption}
              credit={credit}
            />
            {isSpringboard && (
              <div className="fm-cover-page__cta">
                <Button
                  label={t('letsgo')}
                  type={ButtonType.Primary}
                  onClick={handleSpringboardClick}
                />
              </div>
            )}
          </div>
        )}
        <div className={scrollIndicatorContainerClassList}>
          <div className={scrollIndicatorClassList}></div>
        </div>
      </div>
      <div
        style={styleList}
        className="fm-cover-page__ad"
        data-testid="test-fm-cover-page__ad"
      >
        {renderAdComponent}
      </div>
    </div>
  );
};
